/**
 * Follow-up drawer — the header and the 4-cell fact grid (drawer redesign Phase 6b, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Nilay 2026-10-02: "design's look, today's data". The design draws four facts per queue; each cell
 * below is filled ONLY from a field today's drawer (CreateFollowup.vue, FROZEN) already reads out of
 * its result sets (drawerQueues.readDrawerData). A design cell with no field behind it today is not
 * drawn — FACT_GAPS lists it, and a cell from data the drawer DOES load takes its place where one
 * makes sense. Time, formatting and names are INJECTED: `today` 'YYYY-MM-DD', `formatDate(ymd)`,
 * `formatMoney(n)`, `employeeName(id)` — nothing here reads the clock, the store or the mixin.
 *
 * ── FIXED IN THE NEW DRAWER (Nilay 2026-10-02, drawer plan "6a notes" bug 9) ─────────────────
 * Today's header shows "Recall Due" when `RecallDue !== '01/01/1901' || RecallDue !== '01/01/0001'`
 * (CreateFollowup.vue:87, :99) — always true, so Open Dental's "no date" sentinels print as a date
 * (and on unscheduled treatment a NULL RecallDue prints an empty "Recall Due"). isRealOdDate treats
 * 01/01/1901, 01/01/0001 (either spelling, either format) and 0000-00-00 as "no date".
 */

/** The design cells with no data source in today's drawer, per queue (follow-on: needs new data). */
export const FACT_GAPS = {
  OverdueRecallDetails: ['Recall type + interval', 'Insurance 2×/yr preventive flag', 'Last hygienist + provider'],
  unscheduledPatientDetails: ['Tx plan amount (fees) + PPO-adjusted amount', 'Benefits left / annual max (insurance is not loaded for this queue)'],
  AgingDetails: ['Claim fee (only the insurance estimate is loaded)'],
  OutstandingPreAuthDetails: [],
  ProcessedPreAuthDetails: ['Not in the design — cells chosen from the procedures it loads'],
  PatientBalancesDetails: ['Last visit', 'Billing type', 'Guarantor / credit wording (only the amount is loaded)'],
  BrokenAppointmentDetails: ['Production lost'],
};

const SENTINELS = ['0001-01-01', '1901-01-01', '0000-00-00', '01/01/0001', '01/01/1901', '00/00/0000'];

/** An Open Dental date that is really set (not NULL, '', or a "no date" sentinel) — bug 9. */
export function isRealOdDate(value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  if (s === '') return false;
  return !SENTINELS.some(x => s.indexOf(x) === 0);
}

/** 'YYYY-MM-DD' of a 'YYYY-MM-DD[ hh:mm:ss]' or 'MM/DD/YYYY' value, or null. */
export function ymdOf(value) {
  if (typeof value !== 'string') return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : null;
}

function dayNumber(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 864e5 : NaN;
}

/** Whole days from `value` to `today` (positive = in the past), or null. */
export function daysAgo(value, today) {
  const d = dayNumber(ymdOf(value));
  const t = dayNumber(today);
  return isFinite(d) && isFinite(t) ? Math.round(t - d) : null;
}

/** The recall-due cell / status item is shown (bug 9 fixed — today: always, :87 / :99). */
export function recallDueShown(detailPageName, patient) {
  const p = patient || {};
  if (detailPageName === 'OverdueRecallDetails') return isRealOdDate(p.RecallDue);
  if (detailPageName === 'unscheduledPatientDetails') return !p.AptDateTime && isRealOdDate(p.RecallDue);
  return false;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The design's month / day / year chip for a list row, or null when the date is not real. */
export function dateChipOf(value) {
  if (!isRealOdDate(value)) return null;
  const ymd = ymdOf(String(value));
  if (!ymd) return null;
  return { mon: MONTHS[Number(ymd.slice(5, 7)) - 1], day: String(Number(ymd.slice(8, 10))), yr: ymd.slice(0, 4) };
}

// ── Header ───────────────────────────────────────────────────────────────────────────────────

/** Avatar initials from the (already masked) display name: "Quill, Avery" → "AQ". */
export function initialsOf(name) {
  const s = typeof name === 'string' ? name.trim() : '';
  if (!s) return '';
  const parts = s.indexOf(',') !== -1
    ? [s.slice(s.indexOf(',') + 1).trim(), s.slice(0, s.indexOf(',')).trim()]
    : s.split(/\s+/);
  return parts.filter(Boolean).slice(0, 2).map(x => x.charAt(0).toUpperCase()).join('');
}

/** The patient's first name for copy ("…for Avery"): FName, else the part after the comma. */
export function firstNameOf(patient) {
  const p = patient || {};
  if (p.FName) return String(p.FName);
  const n = p.PatientName ? String(p.PatientName) : '';
  return n.indexOf(',') !== -1 ? n.slice(n.indexOf(',') + 1).trim() : n.split(/\s+/)[0] || '';
}

/** Today's contact pills, in today's order (home, mobile, work, email); blanks left out. */
export function contactsOf(patient) {
  const p = patient || {};
  const out = [];
  const add = (kind, value) => { if (value && String(value).trim() !== '') out.push({ kind, value: String(value) }); };
  add('home', p.HmPhone);
  add('mobile', p.WirelessPhone);
  add('work', p.WkPhone);
  add('email', p.Email);
  return out;
}

/** Today's address line (Address, Address2, City, State), or '' when there is no street. */
export function addressOf(patient) {
  const p = patient || {};
  if (!p.Address || String(p.Address).trim() === '') return '';
  return [p.Address, p.Address2, p.City, p.State].filter(x => x && String(x).trim() !== '').join(', ');
}

/**
 * The PRACTICE's texting number (GENERAL_PHONE) for the Text / Payment link forms' "Sends from …"
 * line — today's formattedSmsNumber (CreateFollowup.vue:2876): 11 digits starting with 1 or 10
 * digits → "(555) 555-0100", anything else as saved, '' when unset. Display only: it never decides
 * whether a PATIENT can be texted (that is utils/patientPhone, and only there).
 */
export function practiceNumberLabel(raw) {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const digits = s.replace(/[^0-9]/g, '');
  if (digits.length === 11 && digits.charAt(0) === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  return s;
}

/** Today's premed badge (Premed === 1, CreateFollowup.vue:46). */
export function hasPremed(patient) {
  return !!patient && patient.Premed === 1;
}

// ── Facts ────────────────────────────────────────────────────────────────────────────────────

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function latestBy(rows, field) {
  return (Array.isArray(rows) ? rows : []).reduce((best, r) => {
    const d = r && isRealOdDate(r[field]) ? ymdOf(String(r[field])) : null;
    return d && (!best || d > best.ymd) ? { ymd: d, row: r } : best;
  }, null);
}

function codesOf(rows, field) {
  const codes = (rows || []).map(r => r && r[field]).filter(Boolean);
  if (!codes.length) return '';
  return codes.length > 3 ? `${codes.slice(0, 3).join(', ')} +${codes.length - 3}` : codes.join(', ');
}

function sumOf(rows, field) {
  return (rows || []).reduce((n, r) => n + (Number(r && r[field]) || 0), 0);
}

/**
 * The design cells per queue → [{ key, label, value, sub, tone }] (tone 'danger' = the design's red
 * sub-line). `data` = drawerQueues.readDrawerData(); unknown queue / no data → [].
 */
export function buildDrawerFacts({ detailPageName, data, today, formatDate, formatMoney, employeeName } = {}) {
  if (!data) return [];
  const fd = v => (v && isRealOdDate(v) ? (formatDate ? formatDate(ymdOf(String(v)) || v) : ymdOf(String(v))) : '—');
  const fm = n => (formatMoney ? formatMoney(n) : `$${Number(n || 0).toFixed(2)}`);
  const ago = (v) => {
    const n = daysAgo(v, today);
    if (n === null) return '';
    if (n === 0) return 'Today';
    return n > 0 ? `${plural(n, 'day', 'days')} ago` : `in ${plural(-n, 'day', 'days')}`;
  };
  const p = data.patient || {};
  const fact = (key, label, value, sub, tone) => ({ key, label, value: value === null || value === undefined || value === '' ? '—' : String(value), sub: sub || '', tone: tone || null });
  const assigned = () => {
    const a = data.assignmentRow;
    if (a && a.EmployeeManuallyAssigned === 1 && a.AssignedToEmpID !== null && a.AssignedToEmpID !== undefined) {
      return fact('assigned', 'Assigned to', employeeName ? employeeName(a.AssignedToEmpID) : a.AssignedToEmpID, 'Assigned by hand');
    }
    return fact('assigned', 'Assigned to', 'Not assigned', 'Responsible team in settings');
  };

  switch (detailPageName) {
    case 'OverdueRecallDetails': {
      const out = [];
      if (recallDueShown(detailPageName, p)) {
        const n = daysAgo(p.RecallDue, today);
        if (n !== null && n > 0) out.push(fact('recallDue', 'Recall due', fd(p.RecallDue), `${plural(n, 'day', 'days')} overdue`, 'danger'));
        else out.push(fact('recallDue', 'Recall due', fd(p.RecallDue), n === 0 ? 'Due today' : 'Not due yet'));
      } else {
        out.push(fact('recallDue', 'Recall due', '—', 'No due date in Open Dental'));
      }
      const ins = data.insurance && data.insurance.length > 0 && data.insurance[0].insurance !== null ? data.insurance[0] : null;
      out.push(ins
        ? fact('insurance', 'Insurance', ins.insurance, ins.amountRemaining !== null && ins.amountRemaining !== undefined ? `${fm(ins.amountRemaining)} remaining` : '')
        : fact('insurance', 'Insurance', 'None on file'));
      const last = latestBy(data.pastAppointments, 'AppointmentDate');
      out.push(last ? fact('lastVisit', 'Last visit', fd(last.ymd), last.row.ProcDescript || ago(last.ymd)) : fact('lastVisit', 'Last visit', '—', 'No past visits loaded'));
      const tx = data.pendingTx || [];
      out.push(fact('pendingTx', 'Unscheduled tx', tx.length ? plural(tx.length, 'procedure', 'procedures') : 'None', codesOf(tx, 'ProcCode')));
      return out;
    }
    case 'unscheduledPatientDetails': {
      const tx = data.txInPlan || [];
      const planned = latestBy(tx, 'DateTP');
      const out = [
        planned ? fact('txPlanned', 'Tx planned', fd(planned.ymd), ago(planned.ymd)) : fact('txPlanned', 'Tx planned', '—'),
        fact('treatment', 'Treatment', tx.length ? plural(tx.length, 'procedure', 'procedures') : 'None', codesOf(tx, 'ProcCode')),
        fact('planned', 'Planned appts', (data.plannedAppointments || []).length ? String(data.plannedAppointments.length) : 'None',
          (data.plannedAppointments || [])[0] ? data.plannedAppointments[0].ProcDescript : ''),
      ];
      if (p.AptDateTime) out.push(fact('recall', 'Recall scheduled', fd(p.AptDateTime), 'Add treatment to it'));
      else if (recallDueShown(detailPageName, p)) out.push(fact('recall', 'Recall due', fd(p.RecallDue), 'Not scheduled'));
      else out.push(fact('recall', 'Recall', '—', 'No recall due date'));
      return out;
    }
    case 'AgingDetails': {
      const c = data.claim || {};
      const n = daysAgo(c.DateSent, today);
      const type = c.ClaimType === 'P' ? 'Primary claim' : (c.ClaimType === 'S' ? 'Secondary claim' : (c.ClaimType || ''));
      return [
        fact('sent', 'Claim sent', fd(c.DateSent), n === null ? '' : `${plural(n, 'day', 'days')} ago`, n !== null && n > 90 ? 'danger' : null),
        fact('dos', 'Date of service', fd(c.DateService), type),
        fact('estimate', 'Insurance estimate', c.InsPayEst === null || c.InsPayEst === undefined ? '—' : fm(c.InsPayEst)),
        fact('carrier', 'Carrier', c.CarrierName, c.CarrierPhone),
      ];
    }
    case 'OutstandingPreAuthDetails': {
      const c = data.claim || {};
      const n = daysAgo(c.DateSent, today);
      return [
        fact('sent', 'Sent', fd(c.DateSent), n === null ? '' : `${plural(n, 'day', 'days')} waiting`),
        fact('carrier', 'Carrier', c.Carrier, c.Phone),
        fact('provider', 'Provider', c.Provider, 'Treating dentist'),
        assigned(),
      ];
    }
    case 'ProcessedPreAuthDetails': {
      const procs = data.procedures || [];
      return [
        fact('procedures', 'Procedures', procs.length ? plural(procs.length, 'procedure', 'procedures') : 'None', codesOf(procs, 'ProcedureCode')),
        fact('estimate', 'Insurance estimate', procs.length ? fm(sumOf(procs, 'InsuranceEstimate')) : '—'),
        fact('provider', 'Provider', procs[0] ? procs[0].Provider : ''),
      ];
    }
    case 'PatientBalancesDetails': {
      const bal = data.patientBalance;
      const fc = data.familyClaims || [];
      return [
        fact('balance', 'Balance', bal === null || bal === undefined ? '—' : fm(bal), Number(bal) < 0 ? 'Credit on account' : ''),
        fact('familyClaims', 'Family claims', fc.length ? plural(fc.length, 'claim', 'claims') : 'None', fc.length ? `${fm(sumOf(fc, 'InsuranceEstimate'))} ins est` : ''),
        assigned(),
      ];
    }
    case 'BrokenAppointmentDetails': {
      const hist = data.brokenHistory || [];
      const last = latestBy(hist, 'Date');
      const row = last ? last.row : {};
      return [
        last ? fact('brokenOn', 'Broken on', fd(last.ymd), ago(last.ymd)) : fact('brokenOn', 'Broken on', '—'),
        fact('procedure', 'Procedure', last ? (row.AdjustmentProcedureCode || 'Adjustment') : '—', row.BrokenAppointmentProcDescript),
        fact('fee', 'Fee charged', last && row.Fee !== null && row.Fee !== undefined ? fm(row.Fee) : '—'),
        fact('history', 'Broken history', hist.length ? plural(hist.length, 'entry', 'entries') : 'None'),
      ];
    }
    default:
      return [];
  }
}
